import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Mail,
  Clock,
  CheckCircle2,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Info,
  ChevronDown,
  History,
} from "lucide-react";
import { API } from "../../config";

/**
 * LastEmailWidget — compact panel that surfaces the most recent
 * email sent to a customer, so the dealer sees at-a-glance whether
 * the welcome went out, when the sollecito fired, and whether the
 * customer engaged with the wallet link.
 *
 * Status pill colour code:
 *   • green  → "clicked" (customer signed / activated → strong signal)
 *   • blue   → "opened"  (customer loaded wallet preview → soft signal)
 *   • sky    → "sent" / "delivered" (email left the queue, no engagement yet)
 *   • red    → "failed" (send failed — ESP rejection, invalid address, …)
 *   • gray   → unknown / legacy
 *
 * Open/click signals are mock-derived from wallet events
 * (preview_first_opened_at, contract_signed_at) until AWS SES event
 * publishing is wired up on the LoJack account. The widget renders
 * an amber "stima" hint next to any engagement timestamp when
 * `is_mocked=true`, so the sales rep understands the caveat.
 */
export const LastEmailWidget = ({ customerId, dealerToken, t }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // History timeline — lazy-loaded when the dealer expands the details.
  // Zorzutti CR 01/08/2026: "quale email non ho ricevuto?" fast-answer.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState(null); // null=not-yet-fetched, []=fetched-empty
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const fetchHistory = async () => {
    if (historyLoading || historyItems !== null) return; // one-shot
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await axios.get(
        `${API}/dealer/customers/${customerId}/last-emails`,
        { params: { token: dealerToken, limit: 5 } }
      );
      setHistoryItems(res.data.items || []);
    } catch (err) {
      setHistoryError(
        err.response?.data?.detail || (t.lastEmailHistoryError || "Unable to load history")
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchLastEmail = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(
          `${API}/dealer/customers/${customerId}/last-email`,
          { params: { token: dealerToken } }
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.detail || (t.lastEmailFetchError || "Unable to load")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (customerId && dealerToken) fetchLastEmail();
    return () => { cancelled = true; };
    // `t` is intentionally excluded from the deps — it's only used
    // inside the catch fallback string. If a parent re-creates the
    // translations object on every render, keeping `t` here would
    // cause an unnecessary refetch on every re-render (locale change
    // is far less common than modal-open events).
  }, [customerId, dealerToken]);

  if (loading) {
    return (
      <div
        className="mx-5 mt-4 bg-[#0F0F0F] border border-gray-800 rounded-xl p-4"
        data-testid="last-email-widget-loading"
      >
        <div className="flex items-center gap-3 text-gray-500 text-sm">
          <Mail size={16} className="animate-pulse" />
          <span>{t.lastEmailLoading || "Loading last email…"}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mx-5 mt-4 bg-red-500/5 border border-red-500/20 rounded-xl p-4"
        data-testid="last-email-widget-error"
      >
        <div className="flex items-center gap-3 text-red-400 text-sm">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!data || data.found === false) {
    return (
      <div
        className="mx-5 mt-4 bg-[#0F0F0F] border border-gray-800 rounded-xl p-4"
        data-testid="last-email-widget-empty"
      >
        <div className="flex items-center gap-3 text-gray-500 text-sm">
          <Mail size={16} />
          <span>{t.lastEmailEmpty || "No email has been sent to this customer yet"}</span>
        </div>
      </div>
    );
  }

  // Resolve high-level engagement level for the primary status pill.
  //   clicked > opened > delivered/sent > failed
  const engagementLevel =
    data.clicked_at ? "clicked"
    : data.opened_at ? "opened"
    : (data.status === "failed") ? "failed"
    : "sent";

  const pillStyles = {
    clicked:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    opened:   "bg-sky-500/15 text-sky-300 border-sky-500/40",
    sent:     "bg-lojack-blue-500/15 text-lojack-blue-300 border-lojack-blue-500/40",
    failed:   "bg-red-500/15 text-red-300 border-red-500/40",
  }[engagementLevel];
  const pillIcon = {
    clicked: <MousePointerClick size={12} />,
    opened:  <Eye size={12} />,
    sent:    <CheckCircle2 size={12} />,
    failed:  <AlertTriangle size={12} />,
  }[engagementLevel];
  const pillLabel = {
    clicked: t.lastEmailStatusClicked || "Clicked",
    opened:  t.lastEmailStatusOpened  || "Opened",
    sent:    t.lastEmailStatusSent    || "Sent",
    failed:  t.lastEmailStatusFailed  || "Failed",
  }[engagementLevel];

  const relTime = formatRelativeTime(data.sent_at, t);
  const absTime = formatAbsoluteTime(data.sent_at);

  return (
    <div
      className="mx-5 mt-4 bg-[#0F0F0F] border border-gray-800 rounded-xl p-4"
      data-testid="last-email-widget"
    >
      {/* Header row: title + primary status pill */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide">
          <Mail size={14} className="text-lojack-blue-400" />
          <span>{t.lastEmailTitle || "Last email sent"}</span>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${pillStyles}`}
          data-testid={`last-email-pill-${engagementLevel}`}
        >
          {pillIcon}
          {pillLabel}
        </span>
      </div>

      {/* Body: email type + timestamp + engagement */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-1">
            {t.lastEmailType || "Type"}
          </p>
          <p
            className="text-white font-medium truncate"
            title={data.email_type}
            data-testid="last-email-type"
          >
            {t[`emailType_${data.email_type}`] || data.email_type_label || data.email_type}
          </p>
        </div>
        <div>
          <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-1">
            {t.lastEmailSentAt || "Sent"}
          </p>
          <p
            className="text-white font-medium flex items-center gap-1.5"
            title={absTime || ""}
            data-testid="last-email-sent-at"
          >
            <Clock size={12} className="text-gray-500" />
            {relTime || "—"}
          </p>
        </div>

        {data.opened_at && (
          <div data-testid="last-email-opened-block">
            <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-1 flex items-center gap-1">
              <Eye size={10} className="text-sky-400" />
              {t.lastEmailOpenedAt || "Opened"}
              {data.is_mocked && (
                <span
                  className="text-[9px] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full uppercase tracking-wide ml-1"
                  title={t.lastEmailMockedHint || "Estimate based on wallet activity — real open tracking arrives with AWS SES setup"}
                  data-testid="last-email-mock-badge-opened"
                >
                  {t.lastEmailMockedShort || "est."}
                </span>
              )}
            </p>
            <p className="text-white font-medium text-xs" title={formatAbsoluteTime(data.opened_at)}>
              {formatRelativeTime(data.opened_at, t)}
            </p>
          </div>
        )}

        {data.clicked_at && (
          <div data-testid="last-email-clicked-block">
            <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-1 flex items-center gap-1">
              <MousePointerClick size={10} className="text-emerald-400" />
              {t.lastEmailClickedAt || "Signed / activated"}
              {data.is_mocked && (
                <span
                  className="text-[9px] bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded-full uppercase tracking-wide ml-1"
                  title={t.lastEmailMockedHint || "Estimate based on wallet activity — real click tracking arrives with AWS SES setup"}
                  data-testid="last-email-mock-badge-clicked"
                >
                  {t.lastEmailMockedShort || "est."}
                </span>
              )}
            </p>
            <p className="text-white font-medium text-xs" title={formatAbsoluteTime(data.clicked_at)}>
              {formatRelativeTime(data.clicked_at, t)}
            </p>
          </div>
        )}
      </div>

      {/* Footer: gentle hint about mock signals so the sales rep
          doesn't overreact to a missing "opened" timestamp. */}
      {data.is_mocked && !data.opened_at && !data.clicked_at && (
        <div
          className="mt-3 flex items-start gap-2 text-[11px] text-gray-500 border-t border-gray-800 pt-3"
          data-testid="last-email-mock-hint"
        >
          <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <span>
            {t.lastEmailMockedFooter ||
              "Open & click tracking will be enabled once AWS SES event publishing is configured on the LoJack account."}
          </span>
        </div>
      )}

      {/* Expandable history — lazy-loaded on first open. Answers the
          classic "quale email non ho ricevuto?" question without
          bouncing the dealer to the admin console. Uses a native
          <details> so the browser handles a11y (space/enter to toggle).
          Chevron rotates on open via the `[open]` CSS attribute
          selector. Zorzutti CR 01/08/2026. */}
      <details
        className="group mt-3 border-t border-gray-800 pt-3"
        onToggle={(e) => {
          const isOpen = e.currentTarget.open;
          setHistoryOpen(isOpen);
          if (isOpen) fetchHistory();
        }}
        data-testid="last-email-history-details"
      >
        <summary
          className="flex items-center gap-2 text-[11px] text-gray-400 hover:text-white cursor-pointer select-none marker:content-none list-none"
          data-testid="last-email-history-toggle"
        >
          <History size={12} className="text-gray-500" />
          <span className="uppercase tracking-wide font-semibold">
            {t.lastEmailHistoryTitle || "Recent history"}
          </span>
          <ChevronDown
            size={14}
            className="text-gray-500 ml-auto transition-transform duration-200 group-open:rotate-180"
          />
        </summary>

        <div className="mt-3">
          {historyLoading && (
            <div
              className="flex items-center gap-2 text-[11px] text-gray-500"
              data-testid="last-email-history-loading"
            >
              <Mail size={12} className="animate-pulse" />
              <span>{t.lastEmailHistoryLoading || "Loading history…"}</span>
            </div>
          )}
          {historyError && (
            <div
              className="flex items-center gap-2 text-[11px] text-red-400"
              data-testid="last-email-history-error"
            >
              <AlertTriangle size={12} />
              <span>{historyError}</span>
            </div>
          )}
          {historyOpen && !historyLoading && !historyError && Array.isArray(historyItems) && historyItems.length === 0 && (
            <div
              className="text-[11px] text-gray-500 italic"
              data-testid="last-email-history-empty"
            >
              {t.lastEmailHistoryEmpty || "No prior emails on record"}
            </div>
          )}
          {Array.isArray(historyItems) && historyItems.length > 0 && (
            <ul
              className="space-y-2"
              data-testid="last-email-history-list"
            >
              {historyItems.map((row, idx) => {
                const rowStatus = row.status === "failed" ? "failed" : "sent";
                const dotColor = rowStatus === "failed" ? "bg-red-400" : "bg-lojack-blue-400";
                return (
                  <li
                    key={row.message_id || `${row.email_type}-${idx}`}
                    className="flex items-center gap-2 text-[11px]"
                    data-testid={`last-email-history-row-${idx}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}
                      title={rowStatus === "failed" ? (t.lastEmailStatusFailed || "Failed") : (t.lastEmailStatusSent || "Sent")}
                    />
                    <span
                      className="text-white font-medium truncate flex-1"
                      data-testid={`last-email-history-row-type-${idx}`}
                    >
                      {t[`emailType_${row.email_type}`] || row.email_type_label || row.email_type}
                    </span>
                    <span
                      className="text-gray-500 whitespace-nowrap"
                      title={formatAbsoluteTime(row.sent_at) || ""}
                      data-testid={`last-email-history-row-time-${idx}`}
                    >
                      {formatRelativeTime(row.sent_at, t) || "—"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
};

LastEmailWidget.propTypes = {
  customerId: PropTypes.string.isRequired,
  dealerToken: PropTypes.string.isRequired,
  t: PropTypes.object.isRequired,
};

// ─── Helpers ──────────────────────────────────────────────────────

function formatRelativeTime(iso, t) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHr  = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHr / 24);

  if (diffSec < 60)  return t.timeJustNow    || "just now";
  if (diffMin < 60)  return (t.timeMinutesAgo || "{n} min ago").replace("{n}", diffMin);
  if (diffHr  < 24)  return (t.timeHoursAgo   || "{n} h ago").replace("{n}", diffHr);
  if (diffDay < 30)  return (t.timeDaysAgo    || "{n} d ago").replace("{n}", diffDay);
  // Fall back to absolute date for older records — a relative "3
  // months ago" is less useful than an actual timestamp for triage.
  return d.toLocaleDateString();
}

function formatAbsoluteTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

export default LastEmailWidget;
